import { useRef, useState, type KeyboardEvent } from 'react'
import { LandingIcon } from './icons'
import styles from './ProductPreview.module.css'
import {
  sorteeLandingHeroDashboard,
  sorteeLandingDashboardStats,
} from '../content/sorteeLandingContent'

const views = [
  {
    id: 'overview',
    label: 'Visão geral',
    title: 'Um olhar sobre a sua campanha.',
    subtitle: 'Campanha Novos Horizontes',
    metrics: [
      ['Receita confirmada', 'R$ 12.480'],
      ['Números vendidos', '2.496'],
      ['Participantes', '624'],
    ],
    chartTitle: 'Evolução da campanha',
    chartNote: 'Últimos 7 dias',
  },
  {
    id: 'participants',
    label: 'Participantes',
    title: 'Cada participação tem uma história.',
    subtitle: 'Conheça quem faz parte da sua campanha',
    metrics: [
      ['Participantes', '624'],
      ['Pedidos confirmados', '832'],
      ['Números por pedido', '3'],
    ],
    chartTitle: 'Participações confirmadas',
    chartNote: 'Exemplo de acompanhamento',
  },
  {
    id: 'affiliates',
    label: 'Afiliados',
    title: 'Conexões que ampliam seu alcance.',
    subtitle: 'Acompanhe a sua rede de divulgação',
    metrics: [
      ['Afiliados ativos', '24'],
      ['Pedidos por indicação', '208'],
      ['Receita por indicação', 'R$ 3.120'],
    ],
    chartTitle: 'Rede de afiliados',
    chartNote: 'Exemplo de acompanhamento',
  },
] as const

export function ProductPreview({
  compact = false,
  original = false,
}: {
  compact?: boolean
  original?: boolean
}) {
  const [activeIndex, setActiveIndex] = useState(0)
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  const view =
    original && activeIndex === 0
      ? {
          ...views[0],
          title: sorteeLandingHeroDashboard.title,
          subtitle: sorteeLandingHeroDashboard.subtitle,
          metrics: sorteeLandingDashboardStats.map((item) => [item.label, item.value]),
          chartTitle: sorteeLandingHeroDashboard.revenueLegend,
          chartNote: sorteeLandingHeroDashboard.growthLabel,
        }
      : views[activeIndex]
  function handleKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next = index
    if (event.key === 'ArrowRight') next = (index + 1) % views.length
    else if (event.key === 'ArrowLeft') next = (index + views.length - 1) % views.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = views.length - 1
    else return
    event.preventDefault()
    setActiveIndex(next)
    buttons.current[next]?.focus()
  }
  return (
    <div
      className={`${styles.preview}${compact ? ` ${styles.compact}` : ''}${original ? ` ${styles.original}` : ''}`}
    >
      <div className={styles.toolbar}>
        <span className={styles.wordmark}>
          <img src="/logo/sortee.png" alt="Logo Sortee" width="2089" height="753" />
        </span>
        <span className={styles.toolbarTitle}>Seu espaço de gestão</span>
        <span className={styles.demoBadge}>PAINEL DEMONSTRATIVO</span>
      </div>
      <div className={styles.workspace}>
        <div className={styles.sidebar} aria-hidden="true">
          <span className={styles.workspaceLabel}>EXEMPLO DE PAINEL</span>
          <span className={styles.sidebarActive}>
            <LandingIcon name="dashboard" /> Dashboard
          </span>
          <span>
            <LandingIcon name="page" /> Campanhas
          </span>
          <span>
            <LandingIcon name="people" /> Participantes
          </span>
          <span>
            <LandingIcon name="diagonal" /> Afiliados
          </span>
          <div className={styles.sidebarBottom}>
            <span className={styles.avatar}>NH</span>
            <span>
              Novos Horizontes<small>Minha organização</small>
            </span>
          </div>
        </div>
        <div className={styles.content}>
          <p className={styles.previewHint}>Selecione uma aba para explorar o painel.</p>
          <div
            className={styles.tabs}
            role="tablist"
            aria-label="Explorar recursos do painel"
          >
            {views.map((item, index) => (
              <button
                key={item.id}
                ref={(element) => {
                  buttons.current[index] = element
                }}
                id={`preview-tab-${item.id}`}
                role="tab"
                aria-selected={index === activeIndex}
                aria-controls="preview-panel"
                tabIndex={index === activeIndex ? 0 : -1}
                onClick={() => setActiveIndex(index)}
                onKeyDown={(event) => handleKey(event, index)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div
            id="preview-panel"
            role="tabpanel"
            aria-labelledby={`preview-tab-${view.id}`}
            tabIndex={0}
            className={styles.panel}
          >
            <div className={styles.panelHeading} key={`heading-${view.id}`}>
              <div>
                <p>{view.subtitle}</p>
                <h3>{view.title}</h3>
              </div>
              <span className={styles.activeBadge}>Campanha ativa</span>
            </div>
            <div className={styles.metrics} key={`metrics-${view.id}`}>
              {view.metrics.map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                  <small>Dados ilustrativos</small>
                </div>
              ))}
            </div>
            <div className={styles.chartCard} key={`chart-${view.id}`}>
              <div className={styles.chartHeading}>
                <strong>{view.chartTitle}</strong>
                <span>{view.chartNote}</span>
              </div>
              {activeIndex === 0 ? (
                <div
                  className={styles.chart}
                  role="img"
                  aria-label="Gráfico ilustrativo de evolução de receita de segunda a domingo"
                >
                  <div className={styles.chartLabels}>
                    <span>R$ 4 mil</span>
                    <span>R$ 2 mil</span>
                    <span>R$ 0</span>
                  </div>
                  <div className={styles.chartBody}>
                    <svg
                      viewBox="0 0 680 150"
                      preserveAspectRatio="none"
                      aria-hidden="true"
                    >
                      <defs>
                        <linearGradient
                          id="sortee-chart-fill"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="0%" stopColor="#9064e8" stopOpacity=".3" />
                          <stop offset="100%" stopColor="#9064e8" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M0 136 C40 137 60 102 110 112 S175 94 225 102 S275 57 330 69 S405 29 450 43 S540 14 570 26 S640 16 680 4 V150 H0Z"
                        fill="url(#sortee-chart-fill)"
                      />
                      <path
                        className={styles.chartLine}
                        d="M0 136 C40 137 60 102 110 112 S175 94 225 102 S275 57 330 69 S405 29 450 43 S540 14 570 26 S640 16 680 4"
                        fill="none"
                        stroke="#4c24cb"
                        strokeWidth="3"
                      />
                    </svg>
                    <div className={styles.days}>
                      {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((day) => (
                        <span key={day}>{day}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.records}>
                  {(activeIndex === 1
                    ? [
                        ['Participante 001', '12 números', 'Confirmado'],
                        ['Participante 002', '8 números', 'Confirmado'],
                        ['Participante 003', '4 números', 'Confirmado'],
                      ]
                    : [
                        ['Afiliado 01', '86 pedidos', 'Link ativo'],
                        ['Afiliado 02', '72 pedidos', 'Link ativo'],
                        ['Afiliado 03', '50 pedidos', 'Link ativo'],
                      ]
                  ).map(([name, amount, status]) => (
                    <div className={styles.record} key={name}>
                      <span className={styles.recordAvatar}>
                        <LandingIcon name={activeIndex === 1 ? 'people' : 'diagonal'} />
                      </span>
                      <strong>{name}</strong>
                      <span>{amount}</span>
                      <span className={styles.recordStatus}>{status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
